import { messages } from '../shared/messages.js';
import { mountBoard } from './board.js';
import { FolderPage } from './folder.js';
import { pageLifecycle, type Unmount } from './page-lifecycle.js';
import { tokenFromLocation, type PageEntry } from './token.js';

/**
 * V02 単体プレビューページ。
 *
 * 正本は [#33](../../../../docs/issues/33-viewer-embed/DESIGN.md)。
 *
 * **V01(embed.js)との差分は「入口と外枠」だけ**で、ビューアシェルと
 * 描画コアは共有する。V02 は [ADR-0003](../../../../docs/adr/0003-embed-js-sdk-with-iframe.md)
 * が定めた **iframe 補助手段の実体**でもあるため、**V02 が単独で成立している
 * ことが V01 の逃げ道の前提**になる。
 *
 * V01 との違い:
 * - Shadow DOM を使わない(埋め込み先の CSS と衝突する相手がいない)
 * - トークンは URL から取る
 * - `CustomEvent` を出さない(購読する相手がいない)
 * - サイズはビューポートに追従する
 *
 * **共有フォルダのリンク(`pf_`)も開く**(#267)。フォルダ名と 1 段のタブを出し、
 * タブで盤面を切り替える(`folder.ts`)。盤面のリンク(`pv_`)は従来どおり。
 *
 * **ロゴを出すリンク(#318)では、盤面の外にロゴを出す。** 盤面のリンクは上部の帯の右端(`board.ts`)、
 * 共有フォルダは見出しの行の右端。**書き出しと埋め込み(V01)には出ない**(ADR-0053)。
 *
 * **ページを離れたら捨て、bfcache から戻ったら作り直す**(#306 —— `page-lifecycle.ts`)。
 */

const container = document.querySelector('#viewer');
if (!(container instanceof HTMLElement)) {
  throw new Error('#viewer が見つかりません');
}

/*
 * ビューポートに追従させる(V01 は埋め込み先の幅に追従)。
 *
 * **`min-height` は `dvh` で二重に書く**(#212)。`vh` はモバイルで
 * **アドレスバーを含まない最大の高さ**なので、バーが出ている間は画面より
 * 大きく、**それだけでスクロールが生まれる。** 非対応のブラウザは
 * 2 つ目の宣言を捨てて `vh` を使う。
 */
document.body.setAttribute(
  'style',
  [
    'margin: 0;',
    'min-height: 100vh;',
    'min-height: 100dvh;',
    'display: flex;',
    'align-items: center;',
    'justify-content: center;',
    'background: #2b2b2f;',
  ].join(' '),
);
container.setAttribute('style', 'width: 100%; max-width: 100vw;');

const entry = tokenFromLocation(globalThis.location);

if (entry === null) {
  // **トークンが取れないのは URL の誤り。** 公開 API を叩く前に止める。
  const notice = document.createElement('p');
  notice.textContent = messages('ja').notAvailable;
  notice.setAttribute('style', 'color: #ffffff; font-family: system-ui, sans-serif;');
  container.append(notice);
} else {
  /*
   * **ページを離れたら捨て、bfcache から戻ったら作り直す**(#306)。
   *
   * 捨てるのは WebGL を解放するため(ブラウザごとに同時数の上限がある)。**bfcache に入るときも
   * 捨てる** —— 戻ると文書はそのまま復元されるが、**このスクリプトは走り直さない**ので、
   * `pageshow`(`persisted`)で作り直さないと空白のままになる。判断は `page-lifecycle.ts`。
   */
  const lifecycle = pageLifecycle(() => mount(container, entry));
  globalThis.addEventListener('pagehide', () => {
    lifecycle.hide();
  });
  globalThis.addEventListener('pageshow', (event) => {
    lifecycle.show(event.persisted);
  });
}

/** 盤面(`pv_`)か共有フォルダ(`pf_`)を作って表示を始める。**捨てる関数を返す。** */
function mount(container: HTMLElement, entry: PageEntry): Unmount {
  if (entry.kind === 'folder') {
    // 共有フォルダ(#267)。外枠(見出しとタブ)はページが持ち、盤面はタブごとのシェルが描く。
    const page = new FolderPage(container, {
      token: entry.token,
      // 同一オリジンで配信する(ビューアと公開 API は CloudFront の後ろ)。
      apiBase: globalThis.location.origin,
    });
    void page.start();
    return () => {
      page.destroy();
    };
  }

  // 盤面(`pv_`)。**ロゴを出すリンクなら、盤面の上に帯を出す**(#318 —— `board.ts`)。
  return mountBoard(container, entry.token);
}
